import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { DashboardStats } from '@/hooks/useDashboardData';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardHeaderProps {
  stats: DashboardStats;
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana' ;
  loading?: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  stats,
  isMobile = false,
  dashboardType = 'venda',
  loading = false,
}) => {
  // Create a safe stats object that handles undefined or null values
  const safeStats = {
    totalProperties: stats?.totalProperties ?? 0,
    avgPrice: stats?.avgPrice ?? 0,
    minPrice: stats?.minPrice ?? 0,
    maxPrice: stats?.maxPrice ?? 0,
    avgPricePerSqMeter: stats?.avgPricePerSqMeter ?? 0,
    avgPricePerUsefulSqMeter: stats?.avgPricePerUsefulSqMeter ?? 0,
    priceIncreasedCount: stats?.priceIncreasedCount ?? 0,
    priceDecreasedCount: stats?.priceDecreasedCount ?? 0,
  };

  const showPriceVariation = dashboardType !== 'sairam-ultima-semana';

  return (
    <>
      <h1 className="text-2xl md:text-3xl font-bold">
        Dashboard - {
          dashboardType === 'venda' ? 'Venda' : 
          dashboardType === 'aluguel' ? 'Aluguel' : 
          dashboardType === 'sairam-ultima-semana' ? 'Saíram Última Semana (Vendas)' : 
          'Entraram Última Semana (Vendas)'
        }
      </h1>

      {isMobile && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 my-2 text-sm text-amber-800">
          <p>Para uma melhor experiência, recomendamos usar o dashboard em um computador.</p>
        </div>
      )}

      <div className={`grid grid-cols-3 md:grid-cols-${showPriceVariation ? '8' : '6'} gap-2 md:gap-4`}>
        {/* Total Properties */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              Total de Imóveis
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold">
                {safeStats.totalProperties.toLocaleString('pt-BR')}
              </div>
            )}
          </CardContent>
        </Card>

            {/* Max Price */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              {dashboardType === 'aluguel' ? 'Maior Aluguel' : 'Maior Valor'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold truncate">
                {safeStats.maxPrice.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </div>
            )}
          </CardContent>
        </Card>
        
  

        {/* Min Price */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              {dashboardType === 'aluguel' ? 'Menor Aluguel' : 'Menor Valor'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold truncate">
                {safeStats.minPrice.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </div>
            )}
          </CardContent>
        </Card>

      {/* Average Price */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              {dashboardType === 'aluguel' ? 'Aluguel Médio' : 'Valor Médio'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold truncate">
                {safeStats.avgPrice.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </div>
            )}
          </CardContent>
        </Card>
        

        {/* Price per Square Meter */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              {dashboardType === 'aluguel' ? 'Aluguel/m² Total' : 'Valor/m² Total'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold truncate">
                {safeStats.avgPricePerSqMeter.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Price per Useful Square Meter */}
        <Card className="col-span-1">
          <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
            <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
              {dashboardType === 'aluguel' ? 'Aluguel/m² Útil' : 'Valor/m² Útil'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
            {loading ? (
              <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
            ) : (
              <div className="text-base md:text-2xl font-bold truncate">
                {safeStats.avgPricePerUsefulSqMeter.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Price Increased Count */}
        {showPriceVariation && (
          <Card className="col-span-1">
            <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                Aumentaram de Preço
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
              {loading ? (
                <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="text-base md:text-2xl font-bold">
                    {safeStats.priceIncreasedCount.toLocaleString('pt-BR')}
                  </div>
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Price Decreased Count */}
        {showPriceVariation && (
          <Card className="col-span-1">
            <CardHeader className="pb-1 pt-3 px-3 md:pb-2 md:pt-4 md:px-4">
              <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground">
                Diminuíram de Preço
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-3 md:pb-4 md:px-4">
              {loading ? (
                <Skeleton className="h-6 w-24 md:h-8 md:w-32" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="text-base md:text-2xl font-bold">
                    {safeStats.priceDecreasedCount.toLocaleString('pt-BR')}
                  </div>
                  <TrendingDown className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default DashboardHeader;